/**
 * useCloudReauth — in-place Google Drive / Dropbox re-authentication.
 *
 * Lets the main data-room screen refresh an expired OAuth token without sending
 * the founder back through onboarding. Encapsulates the expo-auth-session
 * request hooks + their response effects + the token exchange, so the screen
 * only deals with `reconnect(provider)` + status flags.
 *
 * EU-sovereign / founder-authority model: tokens are written to device
 * SecureStore only (by the exchange fns) — nothing leaves the device.
 */

import { useCallback, useEffect, useState } from 'react';
import { handleError } from '@/lib/errors';
import {
  useGoogleAuth,
  exchangeCodeForTokens,
  getDriveUserInfo,
  useGoogleDriveStore,
} from '@/lib/google-drive';
import {
  useDropboxAuth,
  exchangeDropboxCodeForTokens,
  getDropboxUserInfo,
  useDropboxStore,
} from '@/lib/dropbox';

export type ReauthProvider = 'google' | 'dropbox';

export interface CloudReauth {
  /** Start an in-place re-auth for the given provider. */
  reconnect: (provider: ReauthProvider) => Promise<void>;
  /** True while the OAuth browser flow is in progress. */
  reconnecting: boolean;
  /** Flips true once a re-auth completes successfully (consumer can react + reset). */
  justReconnected: boolean;
  /** Acknowledge the justReconnected flag. */
  clearJustReconnected: () => void;
  /** Whether each provider has OAuth credentials configured. */
  configured: { google: boolean; dropbox: boolean };
}

export function useCloudReauth(): CloudReauth {
  const setGoogleConnectionState = useGoogleDriveStore((s) => s.setConnectionState);
  const setGoogleConnectedEmail = useGoogleDriveStore((s) => s.setConnectedEmail);
  const setDropboxConnectionState = useDropboxStore((s) => s.setConnectionState);
  const setDropboxConnectedUser = useDropboxStore((s) => s.setConnectedUser);

  const google = useGoogleAuth();
  const dropbox = useDropboxAuth();

  const [reconnecting, setReconnecting] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);

  // Google OAuth response → exchange + mark connected
  useEffect(() => {
    const res = google.response;
    if (!res) return;
    if (res.type === 'success' && res.params.code) {
      (async () => {
        try {
          if (!google.request?.codeVerifier) throw new Error('Missing code verifier');
          await exchangeCodeForTokens(res.params.code, google.request.codeVerifier, google.redirectUri);
          const info = await getDriveUserInfo();
          setGoogleConnectionState('connected');
          setGoogleConnectedEmail(info?.email ?? null);
          setJustReconnected(true);
        } catch (err) {
          handleError(err, { context: 'useCloudReauth.google' });
          setGoogleConnectionState('error');
        } finally {
          setReconnecting(false);
        }
      })();
    } else {
      // cancel / dismiss / error
      setReconnecting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key on the response object
  }, [google.response]);

  // Dropbox OAuth response → exchange + mark connected
  useEffect(() => {
    const res = dropbox.response;
    if (!res) return;
    if (res.type === 'success' && res.params.code) {
      (async () => {
        try {
          if (!dropbox.request?.codeVerifier) throw new Error('Missing code verifier');
          await exchangeDropboxCodeForTokens(res.params.code, dropbox.request.codeVerifier, dropbox.redirectUri);
          const info = await getDropboxUserInfo();
          setDropboxConnectionState('connected');
          setDropboxConnectedUser(info?.email ?? null, info?.name ?? null);
          setJustReconnected(true);
        } catch (err) {
          handleError(err, { context: 'useCloudReauth.dropbox' });
          setDropboxConnectionState('error');
        } finally {
          setReconnecting(false);
        }
      })();
    } else {
      setReconnecting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key on the response object
  }, [dropbox.response]);

  const reconnect = useCallback(
    async (provider: ReauthProvider) => {
      setJustReconnected(false);
      setReconnecting(true);
      try {
        if (provider === 'google') {
          if (!google.isConfigured) {
            setReconnecting(false);
            return;
          }
          setGoogleConnectionState('connecting');
          await google.promptAsync();
        } else {
          if (!dropbox.isConfigured) {
            setReconnecting(false);
            return;
          }
          setDropboxConnectionState('connecting');
          await dropbox.promptAsync();
        }
      } catch {
        setReconnecting(false);
      }
    },
    [google, dropbox, setGoogleConnectionState, setDropboxConnectionState],
  );

  return {
    reconnect,
    reconnecting,
    justReconnected,
    clearJustReconnected: () => setJustReconnected(false),
    configured: { google: google.isConfigured, dropbox: dropbox.isConfigured },
  };
}
